import React from 'react';
import { useStore } from 'effector-react';
import { getExt } from 'pano-utils';
// @ts-ignore
import subtitlesParser from 'subtitles-parser';
import { ImageType } from '@types';
import { Collapse } from '@components/Collapse';
import { $loadedScene } from '@stores/loadedStore';
import { $sceneMapApi } from '@stores/dataStores';
import ImageContainer from '@components/ImageContainer';
import TextLink from '@components/TextLink';
import Tip from '@components/Tip';
import Button from '@components/Button';
import Image from '@components/Image';
import IconTrash from '@components/icons/IconTrash';
import selectFile from '@utils/selectFile';
import { sendError, sendSuccess, sendWarning } from '@components/Noti';
import { readFilePromise } from '@utils/readFilePromise';
import type { StandardSubtitle } from '@utils/subtitleFormat';
import SidebarApplyTo from '@modules/sidebar/components/SidebarApplyTo';
import { showSceneModal } from '@modules/category';
import useTemplateFeature from '@hooks/useTemplateFeature';
import { SUBTITME_MAX_MIN } from '../constants';

export function SubtitleSetting() {
  const scene = useStore($loadedScene)!;
  const { subtitle, subtitleFileName } = scene.config.autoRotate!;

  const hasSubtitle = useTemplateFeature('autoRotateVideo');

  const handleUploadSubtitle = async () => {
    const files = await selectFile({ accept: '.srt' });
    if (!files) return;
    const file = files[0];
    if (getExt(file.name) !== 'srt') {
      sendWarning('仅支持.srt文件');
      return;
    }
    try {
      const text = await readFilePromise(file);
      const srtList = subtitlesParser.fromSrt(text, true) as StandardSubtitle[];
      const last = srtList[srtList.length - 1];
      if (last?.endTime > SUBTITME_MAX_MIN * 60 * 1000) {
        sendWarning(`字幕时长不能超过${SUBTITME_MAX_MIN}分钟`);
        return;
      }
      $sceneMapApi.updateScene({
        id: scene.id,
        updater: (draft) => {
          draft.config.autoRotate!.subtitle = JSON.stringify(srtList);
          draft.config.autoRotate!.subtitleFileName = file.name;
        },
      });
    } catch (e) {
      sendError(e.message);
    }
  };

  const handleDelete = () => {
    $sceneMapApi.updateScene({
      id: scene.id,
      updater: (draft) => {
        draft.config.autoRotate!.subtitle = '';
        draft.config.autoRotate!.subtitleFileName = '';
      },
    });
  };

  const handleApplyTo = async () => {
    const { idSet } = await showSceneModal({
      title: '选择要应用到的场景',
      subTitle: '注：只能应用到同类型场景中',
      allowedTypes: [scene.type],
      multiple: true,
    });
    if (!idSet) return;
    $sceneMapApi.update((draft) => {
      Array.from(idSet).forEach((_id) => {
        draft.get(_id)!;
        draft.get(_id)!.config.autoRotate!.subtitle = subtitle;
        draft.get(_id)!.config.autoRotate!.subtitleFileName = subtitleFileName;
      });
    });
    sendSuccess('讲解字幕已应用到所选场景');
  };

  if (!hasSubtitle) return null;

  return (
    <Collapse title="讲解字幕" showVip>
      <ImageContainer
        className="h-70"
        placeholder={
          <div className="flex flex-col items-center">
            <p>请选择字幕文件</p>
            <TextLink
              href="https://www.720yun.com/bbs/article?id=1203"
              target="_blank"
            >
              SRT格式字幕制作方式
            </TextLink>
          </div>
        }
      >
        {!!subtitle && (
          <>
            <Image
              src="/imgs/srt.95e0f038fcdad951e3a268eb429d8697.png"
              type={ImageType.sysMedia}
              style={{ width: 48, height: 48 }}
            />
            <div className="ml-10 flex flex-col items-start">
              <div className="mb-5 w-140 truncate text-base text-white">
                {subtitleFileName}
              </div>
              <IconTrash onClick={handleDelete} />
            </div>
          </>
        )}
      </ImageContainer>
      <div className="mt-10 flex items-center justify-between">
        <Tip>支持SRT格式字幕文件</Tip>
        <Button size="sm" onClick={handleUploadSubtitle}>
          上传字幕
        </Button>
      </div>
      <SidebarApplyTo
        title="把讲解字幕应用到..."
        className="mt-20"
        onClick={handleApplyTo}
      />
    </Collapse>
  );
}
